import { defineStore } from 'pinia';
import { getDocs, query, collection, where, limit } from 'firebase/firestore';
import {useFirestore} from 'vuefire'
import { toRaw } from 'vue';
import {useWebsiteConfigurationStore} from '~/stores/websiteConfigurationStore';

export const useSearchStore = defineStore('search', {
  state: () => ({
    uiState: {},
    geocodes: [],
    cities: [],
    currentGeocode: null,
    view: 'maps',
    tabs: [],
    url: null,
    areaCities: {},
    areaDistricts: {},
    areasLoaded: false,
    areasLoading: false,
    geocodeLabels: {},
    geocodeLabelsLoaded: false,
    geocodeLabelsLoading: false
  }),
  getters: {
    isGoogleMaps: (state) => state.view == 'maps',
    isGoogleEarth: (state) => state.view == 'earth'
  },
  actions: {
    async fetchProjectListingByGeocode(geocode) {
      const websiteConfigurationStore = useWebsiteConfigurationStore();
      var db = await useFirestore();
      const querySnapshot = await getDocs(
        query(
          collection(db, 'services/mls/listings'),
          where("listing_id", "==", websiteConfigurationStore.configuration.listingId),
          where("announce_type", "==", 'project-announces'),
          where("announce.project.location.geocode", "==", parseInt(geocode)),
          limit(1)
        )
      );
      if (querySnapshot.docs.length > 0) {
        return querySnapshot.docs[0].data();
      }
    },
    // Loads the tenant's areas.json (cities + districts) so the search refinement
    // lists can display human-readable city.name / district.label while still
    // refining on the slug. Maps are keyed by slug; safe to call repeatedly.
    async loadAreas() {
      if (this.areasLoaded || this.areasLoading) return;
      const websiteConfigurationStore = useWebsiteConfigurationStore();
      const configuration = websiteConfigurationStore.configuration;
      if (!configuration) return;

      const runtimeConfig = useRuntimeConfig();
      const baseUrl = runtimeConfig.public.publicStorageUrl;
      const url = `${baseUrl}websites/${configuration.websiteId}/lists/${configuration.listingId}/areas.json`;

      this.areasLoading = true;
      try {
        const data = await $fetch(url);
        const cities = {};
        (data?.cities ?? []).forEach((city) => {
          cities[city.slug] = city.name;
        });
        const districts = {};
        (data?.districts ?? []).forEach((district) => {
          districts[district.slug] = district.label;
        });
        this.areaCities = cities;
        this.areaDistricts = districts;
        this.areasLoaded = true;
      } catch (error) {
        console.error('Failed to load areas.json', error);
      } finally {
        this.areasLoading = false;
      }
    },
    // Loads the tenant's geocodes.json (building geocode -> name) so the buildings
    // refinement list can display the building name while refining on the geocode.
    // Shape: { "<geocode>": { "name": "..." } }. Safe to call repeatedly.
    async loadGeocodeLabels() {
      if (this.geocodeLabelsLoaded || this.geocodeLabelsLoading) return;
      const websiteConfigurationStore = useWebsiteConfigurationStore();
      const configuration = websiteConfigurationStore.configuration;
      if (!configuration) return;

      const runtimeConfig = useRuntimeConfig();
      const baseUrl = runtimeConfig.public.publicStorageUrl;
      const url = `${baseUrl}websites/${configuration.websiteId}/lists/${configuration.listingId}/geocodes.json`;

      this.geocodeLabelsLoading = true;
      try {
        const data = await $fetch(url);
        const labels = {};
        Object.entries(data ?? {}).forEach(([geocode, value]) => {
          labels[geocode] = value?.name ?? geocode;
        });
        this.geocodeLabels = labels;
        this.geocodeLabelsLoaded = true;
      } catch (error) {
        console.error('Failed to load geocodes.json', error);
      } finally {
        this.geocodeLabelsLoading = false;
      }
    },
    async findLocationByPlaceId(placeId) {
      const websiteConfigurationStore = useWebsiteConfigurationStore();
      const db = await useFirestore();
      const querySnapshot = await getDocs(
        query(
          collection(db, `services/mls/lists/${websiteConfigurationStore.configuration.listingId}/locations`),
          where("gmaps_place_id", "==", placeId),
          limit(1)
        )
      );
      if (querySnapshot.docs.length > 0) {
        return querySnapshot.docs[0].data();
      }
    },
    openTab(label, filter) {
      this.tabs.push({
        label,
        filter
      });
    },
    useEarth(bool) {
      if (bool) {
        this.view = 'earth';
      } else {
        this.view = 'maps';        
      }
    },
    setCurrentGeocode(currentGeocode) {
      if (currentGeocode !== this.currentGeocode) {
        this.currentGeocode = currentGeocode;
      }
    },
    setView(view) {
      this.view = view;
    },
    setFacet(name, items) {
      // Deep comparison to prevent unnecessary updates
      const currentItems = this[name];
      const newItems = toRaw(items);
      
      // Check if the arrays are different
      if (JSON.stringify(newItems) !== JSON.stringify(currentItems)) {
        this[name] = newItems;
      }
    },
    setUiState(uiState) {
      this.uiState = uiState;
    },
    async findLocationBySlug(slug) {
      var db = await useFirestore();
      const querySnapshot = await getDocs(
        query(
          collection(db, "apps/flat-marketplace/public/search/locations"),
          where("slug", "==", slug), // Fetch both project and property listings
          limit(1)
        )
      );

      if (querySnapshot.docs.length > 0) {
        return querySnapshot.docs[0].data();
      }
    }
  }
});
